import Seo from "@/components/Seo";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Auth() {
  const [params] = useSearchParams();
  const mode = params.get("mode") === "register" ? "register" : "login";
  const presetRole = params.get("role");
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState(presetRole === "contractor" ? "contractor" : "customer");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      const dest = user.role === "admin" ? "/admin" : user.role === "contractor" ? "/contractor" : "/customer";
      navigate(dest, { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        const u = await register({ email, password, name, role });
        toast.success("Welcome to LeadTitan!");
        const dest = u.role === "contractor" ? "/contractor" : "/customer";
        navigate(dest);
      } else {
        const u = await login(email, password);
        toast.success("Signed in.");
        const dest = u.role === "admin" ? "/admin" : u.role === "contractor" ? "/contractor" : "/customer";
        navigate(dest);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    sessionStorage.setItem("oauth_role", role);
    const redirect = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen bg-bone">
      <Seo title="Sign In or Join" path="/auth" noindex />
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 hidden lg:block">
          <div className="brut-card p-10 bg-ink text-white h-full">
            <div className="overline text-acid">// {mode === "register" ? "Join" : "Welcome back"}</div>
            <h2 className="font-display font-black text-5xl uppercase mt-4 leading-none">
              {mode === "register" ? (
                <>Start working <span className="text-acid">smarter.</span></>
              ) : (
                <>Pick up where you <span className="text-acid">left off.</span></>
              )}
            </h2>
            <p className="text-white/70 mt-6">
              {role === "contractor"
                ? "Pay only for the leads you want. No commissions, no contracts."
                : "Post your project. Get matched with vetted pros in your area."}
            </p>
            <div className="mt-10 space-y-3 font-mono text-xs uppercase tracking-widest">
              <div>// 14 verified trades</div>
              <div>// Real customer contact info</div>
              <div>// Stripe + PayPal billing</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="brut-card p-8 md:p-12 bg-white">
            <div className="flex gap-0 border-2 border-ink mb-8">
              <Link to="/auth?mode=login" className={`flex-1 text-center py-3 font-mono uppercase text-xs tracking-widest font-bold ${mode === "login" ? "bg-ink text-acid" : "bg-white"}`} data-testid="tab-login">Sign in</Link>
              <Link to="/auth?mode=register" className={`flex-1 text-center py-3 font-mono uppercase text-xs tracking-widest font-bold border-l-2 border-ink ${mode === "register" ? "bg-ink text-acid" : "bg-white"}`} data-testid="tab-register">Create account</Link>
            </div>

            <form onSubmit={submit} className="space-y-5">
              {mode === "register" && (
                <>
                  <div>
                    <label className="overline block mb-2">I am a</label>
                    <div className="grid grid-cols-2 gap-0 border-2 border-ink">
                      <button type="button" onClick={() => setRole("customer")} className={`py-3 font-mono uppercase text-xs tracking-widest font-bold ${role === "customer" ? "bg-klein text-white" : "bg-white"}`} data-testid="role-customer">Customer</button>
                      <button type="button" onClick={() => setRole("contractor")} className={`py-3 font-mono uppercase text-xs tracking-widest font-bold border-l-2 border-ink ${role === "contractor" ? "bg-acid text-ink" : "bg-white"}`} data-testid="role-contractor">Contractor / Pro</button>
                    </div>
                  </div>
                  <div>
                    <label className="overline block mb-2">Full name</label>
                    <input type="text" className="brut-input" value={name} onChange={(e) => setName(e.target.value)} required data-testid="input-name" />
                  </div>
                </>
              )}
              <div>
                <label className="overline block mb-2">Email</label>
                <input type="email" className="brut-input" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="input-email" />
              </div>
              <div>
                <label className="overline block mb-2">Password</label>
                <input type="password" className="brut-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="input-password" />
                {mode === "login" && (
                  <div className="mt-2 text-right">
                    <Link to="/forgot-password" className="font-mono text-xs underline hover:text-klein" data-testid="forgot-password-link">
                      Forgot password?
                    </Link>
                  </div>
                )}
              </div>
              <button disabled={loading} type="submit" className="brut-btn-primary w-full justify-center" data-testid="submit-auth">
                {loading ? "Please wait…" : mode === "register" ? "Create account" : "Sign in"}
              </button>
            </form>

            <div className="my-6 flex items-center gap-4">
              <div className="flex-1 h-px bg-ink" />
              <span className="font-mono text-xs uppercase tracking-widest">or</span>
              <div className="flex-1 h-px bg-ink" />
            </div>

            <button onClick={googleSignIn} className="brut-btn w-full justify-center" data-testid="google-signin">
              <span>Continue with Google</span>
            </button>

            {mode === "login" && (
              <p className="mt-6 text-xs font-mono text-muted-foreground">
                Try admin: <span className="font-bold">support@lead-titan.net / admin123</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
